<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { choiceVisualState, correctAnswerIds, moveRankingOption, rankingVisualState } from '../questionState';
import type { Question, QuestionFeedback, QuestionOption } from '../types';
import type { TranslationKey } from '../i18n';

const props = defineProps<{
	question: Question;
	questionNumber: number;
	questionTotal: number;
	selected: string[];
	rankOrder: string[];
	guess: string;
	guessAttemptsRemaining: number;
	guessRetry: boolean;
	remainingMs: number;
	durationMs: number;
	busy: boolean;
	feedback: QuestionFeedback | null;
	showCorrectResponses: boolean;
	showStreak: boolean;
	streakMultiplier: number;
	t: (key: TranslationKey) => string;
}>();

const emit = defineEmits<{
	'update:selected': [value: string[]];
	'update:rankOrder': [value: string[]];
	'update:guess': [value: string];
	submit: [];
	continue: [];
	videoReady: [];
}>();

const revealRemaining = ref(0);
const videoLoaded = ref(false);
const videoFailed = ref(false);
const guessInput = ref<HTMLInputElement | null>(null);
const draggedId = ref('');
const dragTargetId = ref('');
let revealTimer: number | undefined;

const options = computed<QuestionOption[]>(() => {
	if(props.question.type === 'tf') {
		return [
			{ id: 't', text: props.t('trueLabel'), imageUrl: '' },
			{ id: 'f', text: props.t('falseLabel'), imageUrl: '' },
		];
	}
	return props.question.options;
});

const orderedOptions = computed(() =>
	props.rankOrder
		.map((id) => options.value.find((option) => option.id === id))
		.filter((option): option is QuestionOption => Boolean(option)),
);

const timerPercent = computed(() =>
	props.durationMs > 0 ? Math.max(0, Math.min(100, props.remainingMs / props.durationMs * 100)) : 100,
);
const timerSeconds = computed(() => Math.max(0, Math.ceil(props.remainingMs / 1000)));
const timerWarning = computed(() => props.remainingMs > 0 && props.remainingMs <= 5000);

const videoEmbedUrl = computed(() => {
	const url = props.question.videoUrl;
	const youtube = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/i);
	if(youtube) return `https://www.youtube.com/embed/${youtube[1]}?autoplay=0&rel=0`;
	const vimeo = url.match(/vimeo\.com\/(\d+)/i);
	if(vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
	return '';
});

const videoLocked = computed(() =>
	props.question.type === 'vmc4' && (!videoLoaded.value || revealRemaining.value > 0),
);
const questionVisible = computed(() => props.question.type !== 'vmc4' || !videoLocked.value);
const submitted = computed(() => Boolean(props.feedback));

const canSubmit = computed(() => {
	if(props.busy || submitted.value || videoLocked.value) return false;
	if(props.question.type === 'gw') return props.guess.trim().length > 0;
	if(props.question.type === 'ro') return props.rankOrder.length === options.value.length;
	return props.selected.length > 0;
});
const canContinue = computed(() => Boolean(props.feedback) && !props.busy);

const guessLength = computed(() => Math.max(1, Math.floor(props.question.answerLength)));
const compactGuess = computed(() => Array.from(props.guess.replace(/\s/g, '')).slice(0, guessLength.value));
const guessBoxes = computed(() => Array.from({ length: guessLength.value }, (_, index) => compactGuess.value[index] ?? ''));

const correctAnswerLabel = computed(() => {
	if(!props.feedback?.correctResponse) return '';
	if(props.question.type === 'gw') return props.feedback.correctResponse;
	const ids = correctAnswerIds(props.question, props.feedback.correctResponse);
	if(props.question.type === 'tf') return ids[0] === 't' ? props.t('trueLabel') : props.t('falseLabel');
	const labels = ids.map((id) => options.value.find((option) => option.id === id)?.text || id.toUpperCase());
	return labels.join(props.question.type === 'ro' ? ' → ' : ', ');
});

const feedbackTitle = computed(() => {
	if(props.feedback?.timedOut) return props.t('timeExpired');
	return props.feedback?.correct ? props.t('correct') : props.t('incorrect');
});

const selectOption = (id: string): void => {
	if(props.busy || submitted.value || videoLocked.value) return;
	if(props.question.type === 'ms') {
		emit('update:selected', props.selected.includes(id)
			? props.selected.filter((value) => value !== id)
			: [...props.selected, id]);
		return;
	}
	emit('update:selected', [id]);
};

const drop = (targetId: string): void => {
	const sourceId = draggedId.value;
	draggedId.value = '';
	dragTargetId.value = '';
	if(!sourceId || sourceId === targetId || props.busy || submitted.value) return;
	const order = [...props.rankOrder];
	const from = order.indexOf(sourceId);
	const to = order.indexOf(targetId);
	if(from < 0 || to < 0) return;
	order.splice(to, 0, order.splice(from, 1)[0]);
	emit('update:rankOrder', order);
};

const startRankDrag = (optionId: string): void => {
	if(props.busy || submitted.value) return;
	draggedId.value = optionId;
};

const endRankDrag = (): void => {
	draggedId.value = '';
	dragTargetId.value = '';
};

const moveRank = (optionId: string, offset: -1 | 1): void => {
	if(props.busy || submitted.value) return;
	const order = moveRankingOption(props.rankOrder, optionId, offset);
	if(order !== props.rankOrder) emit('update:rankOrder', order);
};

const mediaLoaded = (): void => {
	window.clearInterval(revealTimer);
	videoLoaded.value = true;
	videoFailed.value = false;
	revealRemaining.value = props.question.videoRevealDurationSeconds;
	if(revealRemaining.value <= 0) {
		emit('videoReady');
		return;
	}
	revealTimer = window.setInterval(() => {
		revealRemaining.value--;
		if(revealRemaining.value <= 0) {
			window.clearInterval(revealTimer);
			emit('videoReady');
		}
	}, 1000);
};

const mediaError = (): void => {
	window.clearInterval(revealTimer);
	videoFailed.value = true;
	videoLoaded.value = false;
};

const resetVideo = (): void => {
	window.clearInterval(revealTimer);
	videoLoaded.value = false;
	videoFailed.value = false;
	revealRemaining.value = 0;
};

const focusGuess = (): void => guessInput.value?.focus();

const updateGuess = (event: Event): void => {
	const input = event.target as HTMLInputElement;
	const value = Array.from(input.value.replace(/\s/g, '')).slice(0, guessLength.value).join('');
	input.value = value;
	emit('update:guess', value);
};

watch(() => props.question.id, resetVideo, { immediate: true });
onBeforeUnmount(() => window.clearInterval(revealTimer));
</script>

<template>
	<article class="question-card">
		<div class="quiz-layout">
			<aside class="quiz-prompt-panel">
				<div class="question-category-row">
					<span v-if="question.category" class="category-pill">{{ question.category }}</span>
					<span class="question-count">{{ questionNumber }} {{ t('of') }} {{ questionTotal }}</span>
				</div>

				<img v-if="question.imageUrl" class="question-image" :src="question.imageUrl" alt="">

				<div v-if="question.type === 'vmc4'" class="video-region">
					<div v-if="videoFailed" class="media-error">
						<p class="error-text">{{ t('videoError') }}</p>
						<button type="button" class="secondary-action" @click="resetVideo">{{ t('retryVideo') }}</button>
					</div>
					<iframe
						v-else-if="videoEmbedUrl"
						:key="question.id"
						:src="videoEmbedUrl"
						title="Question video"
						allow="autoplay; encrypted-media; picture-in-picture"
						allowfullscreen
						@load="mediaLoaded"
					/>
					<video
						v-else
						:key="question.id"
						:src="question.videoUrl"
						controls
						playsinline
						@loadeddata="mediaLoaded"
						@error="mediaError"
					/>
					<p v-if="!videoLoaded && !videoFailed" class="muted">{{ t('videoLoading') }}</p>
					<div v-if="revealRemaining > 0" class="video-waiting">
						{{ t('videoReveal') }} <strong>{{ revealRemaining }}</strong> {{ t('secondsLong') }}
					</div>
				</div>

				<h1 v-if="questionVisible" class="question-title" :class="{ 'is-long': question.text.length > 135 }">{{ question.text }}</h1>
			</aside>

			<section class="quiz-answer-panel">
				<div v-if="durationMs > 0 && !videoLocked" class="question-timer" :class="{ 'is-warning': timerWarning }" aria-label="Question timer">
					<div class="timer-track"><div class="timer-fill" :style="{ width: `${timerPercent}%` }" /></div>
					<div class="timer-value">{{ timerSeconds }} {{ t('secondsShort') }}</div>
				</div>

				<div class="question-body">
					<div v-if="questionVisible && question.type === 'gw'" class="guess-region">
						<p v-if="question.hint" class="template-note"><strong>{{ t('hint') }}:</strong> {{ question.hint }}</p>
						<div class="guess-input-shell" @click="focusGuess">
							<div class="letter-boxes" aria-hidden="true">
								<span v-for="(letter, index) in guessBoxes" :key="index" class="letter-box" :class="{ 'is-filled': letter }">{{ letter }}</span>
							</div>
							<input
								ref="guessInput"
								class="guess-input"
								:value="guess"
								type="text"
								autocomplete="off"
								autocapitalize="characters"
								spellcheck="false"
								aria-label="Type your answer"
								:maxlength="guessLength"
								:disabled="busy || submitted"
								@input="updateGuess"
								@keyup.enter="canSubmit && emit('submit')"
							>
						</div>
						<p class="template-note">{{ t('attemptsLeft') }}: <strong>{{ guessAttemptsRemaining }}</strong></p>
					</div>

					<div v-else-if="questionVisible && question.type === 'ro'">
						<p class="template-note">{{ t('rankHelp') }}</p>
						<TransitionGroup name="rank-shift" tag="div" class="answer-list rank-list">
							<div
								v-for="(option, index) in orderedOptions"
								:key="option.id"
								class="answer-option ranking-option"
								:class="[
									rankingVisualState(option.id, index, feedback, showCorrectResponses),
									{ 'drag-target': dragTargetId === option.id && draggedId !== option.id },
								]"
								@dragenter.prevent="dragTargetId = option.id"
								@dragover.prevent
								@drop="drop(option.id)"
							>
								<span class="answer-letter ranking-position">{{ index + 1 }}</span>
								<span class="answer-label">{{ option.text }}</span>
								<span
									class="ranking-drag-handle"
									:class="{ 'is-disabled': busy || submitted }"
									:draggable="!busy && !submitted"
									aria-hidden="true"
									@dragstart="startRankDrag(option.id)"
									@dragend="endRankDrag"
								>⋮⋮</span>
								<span class="ranking-controls">
									<button
										v-if="index !== 0"
										type="button"
										class="ranking-move-button"
										:aria-label="`${t('moveUp')}: ${option.text}`"
										:disabled="busy || submitted"
										@click.stop="moveRank(option.id, -1)"
									>↑</button>
									<button
										v-if="index !== orderedOptions.length - 1"
										type="button"
										class="ranking-move-button"
										:aria-label="`${t('moveDown')}: ${option.text}`"
										:disabled="busy || submitted"
										@click.stop="moveRank(option.id, 1)"
									>↓</button>
								</span>
							</div>
						</TransitionGroup>
					</div>

					<div v-else-if="questionVisible">
						<p v-if="question.type === 'ms'" class="template-note">{{ t('selectAll') }}</p>
						<div :class="question.type === 'pa' ? 'photo-grid' : 'answer-list'">
							<button
								v-for="option in options"
								:key="option.id"
								type="button"
								class="answer-option"
								:class="[
									{ 'photo-option': question.type === 'pa', 'has-long-label': option.text.length > 105 },
									choiceVisualState(question, option.id, selected, feedback, showCorrectResponses),
								]"
								:aria-pressed="selected.includes(option.id)"
								:disabled="busy || submitted || videoLocked"
								@click="selectOption(option.id)"
							>
								<img v-if="question.type === 'pa'" :src="option.imageUrl" :alt="option.text">
								<span class="answer-letter">{{ option.id.toUpperCase() }}</span>
								<span v-if="question.type !== 'pa'" class="answer-label">{{ option.text }}</span>
							</button>
						</div>
					</div>
				</div>

				<div
					v-if="feedback || guessRetry"
					class="question-feedback"
					:class="guessRetry ? 'partial' : feedback?.correct ? 'correct' : 'incorrect'"
					role="status"
					aria-live="polite"
				>
					<div class="question-feedback-summary">
						<strong>{{ guessRetry ? t('retryAnswer') : feedbackTitle }}</strong>
						<template v-if="feedback">
							<span>{{ feedback.award > 0 ? '+' : '' }}{{ feedback.award }} {{ t('points') }}.</span>
							<span v-if="showStreak" class="inline-streak">×{{ streakMultiplier }} {{ t('streak') }}.</span>
						</template>
					</div>
					<div v-if="feedback && showCorrectResponses && !feedback.correct && correctAnswerLabel" class="question-feedback-detail"><strong>{{ t('correctAnswer') }}:</strong> {{ correctAnswerLabel }}.</div>
					<div v-if="feedback?.explanation" class="question-feedback-detail">{{ feedback.explanation }}</div>
				</div>

				<div class="question-actions">
					<button type="button" :class="canSubmit ? 'primary-action' : 'secondary-action'" :disabled="!canSubmit" @click="emit('submit')">
						<span v-if="busy" class="button-spinner" aria-hidden="true" />
						{{ t('submit') }}
					</button>
					<button type="button" :class="canContinue ? 'primary-action' : 'secondary-action'" :disabled="!canContinue" @click="emit('continue')">{{ t('continue') }}</button>
				</div>
			</section>
		</div>
	</article>
</template>
